/**
 * Fit
 *
 * The one contain-fit of the placement system. Used by the relative rule,
 * by every box-based rule (`fitIntoBox`), by the KLEIN and RUNDUM branches
 * and by the QC validation for its "contained" size. Lives in its own
 * module so the rule modules can share it without importing each other.
 */

import type { RelativeDesignState } from './placementTypes.js';
import type { PlacementArea } from '../placement/sizeTier.js';
import { OVERFLOW_TOLERANCE_REL } from '../placement/tolerance.js';

/** Design height as a fraction of the print area height. */
export function relHeightOn(
  rel: RelativeDesignState,
  area: PlacementArea
): number {
  return (rel.relWidth * rel.designRatio * area.width) / area.height;
}

/**
 * Whether the design pokes out of the print area on any side by more than
 * the shared tolerance. An overflow of exactly the tolerance still fits.
 */
export function overflows(
  rel: RelativeDesignState,
  area: PlacementArea
): boolean {
  const halfW = rel.relWidth / 2;
  const halfH = relHeightOn(rel, area) / 2;
  // Floating-point slack so an overflow of exactly the tolerance is inside.
  const tol = OVERFLOW_TOLERANCE_REL + 1e-9;
  return (
    rel.relCenterX - halfW < -tol ||
    rel.relCenterX + halfW > 1 + tol ||
    rel.relCenterY - halfH < -tol ||
    rel.relCenterY + halfH > 1 + tol
  );
}

/**
 * The one fit calculation: shrink uniformly until the design is at most
 * 100 % of the area on both axes, then clamp the center so every edge is
 * inside.
 */
export function fitInside(
  rel: RelativeDesignState,
  area: PlacementArea
): RelativeDesignState {
  const relHeight = relHeightOn(rel, area);
  const shrink = Math.min(1, 1 / rel.relWidth, 1 / relHeight);
  const relWidth = rel.relWidth * shrink;
  const fittedHeight = relHeight * shrink;

  const clamp = (center: number, half: number): number =>
    Math.max(half, Math.min(1 - half, center));

  return {
    relCenterX: clamp(rel.relCenterX, relWidth / 2),
    relCenterY: clamp(rel.relCenterY, fittedHeight / 2),
    relWidth,
    designRatio: rel.designRatio,
  };
}
